import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { _className, ComponentSync, EditableDBItemV3, genericNotificationAction, Grid, LL_H_C, LL_V_L, ModuleFE_MouseInteractivity, ModuleFE_Print, mouseInteractivity_PopUp, openContent, TS_Printable } from '@nu-art/thunderstorm-frontend/index';
import { dbObjectToId, sortArray } from '@nu-art/ts-common';
import { PermissionKey_DiseaseProfile_Delete, PermissionKey_DiseaseProfile_Edit } from '@app/dp-shared';
import { ICONS } from '@app/styles';
import './Manager_DiseaseProfile.scss';
import { Printable_DiseaseProfile } from '../printable/index.js';
import { ModuleFE_Tag, ModuleFE_Variable } from '@app/hcs-frontend';
import { Renderer_GeneralInfo } from './renderers/Renderer_GeneralInfo.js';
import { Renderer_DiseaseVariableData } from './renderers/Renderer_DiseaseVariableData.js';
import { AccessLevel, ModuleFE_PermissionsAssert } from '@nu-art/permissions-frontend/modules/ModuleFE_PermissionsAssert';
import { ConfigKeyFE_DPViewOrder, ModuleFE_ColorGroup, ModuleFE_DiseaseProfile, ModuleFE_DiseaseView, ModuleFE_MultiVar } from '../../_entity.js';
const varOptions = ['Overview', 'Disease Info', 'Snomed Info', 'Epidemiology', 'Prognosis', 'Scores', 'References'];
const editModeVarOptions = ['Overview', 'Epidemiology', 'Prognosis', 'Scores', 'References'];
export class Manager_DiseaseProfile extends ComponentSync {
    // ######################## Static ########################
    static defaultProps = {
        modules: [ModuleFE_DiseaseView, ModuleFE_ColorGroup]
    };
    DP_PermissionKey_Edit = ModuleFE_PermissionsAssert.getPermissionKey(PermissionKey_DiseaseProfile_Edit);
    DP_PermissionKey_Delete = ModuleFE_PermissionsAssert.getPermissionKey(PermissionKey_DiseaseProfile_Delete);
    // ######################## Life Cycle ########################
    deriveStateFromProps(nextProps, state) {
        const firstDerive = !this.state?.diseaseProfile;
        state ??= this.state ? { ...this.state } : {};
        //Set initial disease profile or if the id has changed
        if (!state.diseaseProfile || state.diseaseProfile.item._id !== nextProps.diseaseProfileId)
            state.diseaseProfile = new EditableDBItemV3(ModuleFE_DiseaseProfile.cache.unique(nextProps.diseaseProfileId), ModuleFE_DiseaseProfile);
        //Set initial edit mode
        state.editMode ??= false;
        //Set initial view
        state.selectedViewId ??= 'general';
        //Set initial varIds
        state.varIds ??= varOptions;
        state.selectedVarId ??= state.varIds[0];
        //If a multiVar id is given
        if (nextProps.multiVarId) {
            //If not the first derive or the multiVar id hasn't changed
            if (!firstDerive && this.props.multiVarId === nextProps.multiVarId)
                return state;
            const views = ModuleFE_DiseaseView.cache.all();
            for (const view of views) {
                const varIds = ModuleFE_MultiVar.cache.filter(mv => mv.diseaseProfileId === state.diseaseProfile.item._id
                    && ModuleFE_DiseaseView.filterMultiVarByEnforcedTag(view, mv)).map(dbObjectToId);
                //If this view holds the given multiVar id
                if (varIds.includes(nextProps.multiVarId)) {
                    state.selectedViewId = view._id;
                    state.varIds = varIds;
                    state.selectedVarId = nextProps.multiVarId;
                    break;
                }
            }
        }
        return state;
    }
    // ######################## Logic ########################
    headerAction__ExportPDF = () => {
        ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
        if (!this.state.printableRef)
            return;
        ModuleFE_Print.printElement(this.state.printableRef, [{ key: 'theme', value: 'light' }]);
    };
    headerAction__editDisease = () => {
        ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
        const varIds = this.state.selectedViewId === 'general' ? editModeVarOptions : this.state.varIds;
        const selectedVarId = varIds.includes(this.state.selectedVarId) ? this.state.selectedVarId : varIds[0];
        this.setState({ editMode: true, varIds, selectedVarId });
    };
    headerAction__addVariable = () => {
        ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
    };
    headerAction__PublishDisease = async () => {
        ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
        const dp = this.state.diseaseProfile;
        this.state.diseaseProfile.set('completed', !dp.item.completed);
        await genericNotificationAction(async () => {
            await dp.save();
            this.forceUpdate();
        }, {
            inProgress: 'Saving Disease Profile',
            success: 'Save Disease Profile - Success',
            failed: 'Save Disease Profile - Failed',
        });
    };
    headerAction__DeleteDisease = () => {
        ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
    };
    headerAction__ExitEdit = () => {
        const varIds = this.state.selectedViewId === 'general' ? varOptions : this.state.varIds;
        this.setState({ editMode: false, varIds });
    };
    getOrderedViews = () => {
        const views = ModuleFE_DiseaseView.cache.allMutable();
        const order = ConfigKeyFE_DPViewOrder.get();
        return sortArray(views, view => order.indexOf(view._id));
    };
    setView = (selectedViewId) => {
        if (selectedViewId === this.state.selectedViewId)
            return;
        let varIds;
        if (selectedViewId === 'general')
            varIds = this.state.editMode ? editModeVarOptions : varOptions;
        else {
            const view = ModuleFE_DiseaseView.cache.unique(selectedViewId);
            varIds = ModuleFE_MultiVar.cache.filter(mv => mv.diseaseProfileId === this.state.diseaseProfile.item._id
                && ModuleFE_DiseaseView.filterMultiVarByEnforcedTag(view, mv)).map(dbObjectToId);
        }
        this.setState({
            selectedViewId,
            varIds,
            selectedVarId: varIds[0]
        });
    };
    triggerChange = (dp) => {
        if (dp === this.state.diseaseProfile)
            return this.forceUpdate();
        this.setState({ diseaseProfile: dp });
    };
    // ######################## Render - Structure ########################
    render() {
        const dp = this.state.diseaseProfile.item;
        return _jsxs(_Fragment, { children: [_jsxs(Grid, { id: 'manager__disease-profile', className: 'quai-item-manager', children: [this.renderHeader(), this.renderViews(), this.renderVars(), this.renderData()] }), dp._id &&
                    _jsx(TS_Printable, { printOnly: true, printable: async (printableRef) => this.setState({ printableRef }), children: _jsx(Printable_DiseaseProfile, { diseaseProfileId: dp._id }) })] });
    }
    // ######################## Render - Header ########################
    renderHeader = () => {
        const dp = this.state.diseaseProfile.item;
        const editMode = this.state.editMode;
        const className = _className('manager__header', editMode && 'edit-mode');
        return _jsx(LL_H_C, { className: className, children: _jsxs(LL_V_L, { className: 'manager__header__main', children: [_jsxs(LL_H_C, { className: 'manager__header__title', children: [_jsx("span", { className: 'manager__header__title__label', children: dp.label }), this.renderDPColor(), !editMode &&
                                _jsx(ICONS.more.component, { ...openContent.popUp.bottom('disease-profile-manager__header-actions', this.renderHeaderActions) })] }), _jsxs(LL_H_C, { className: 'manager__header__sub-title', children: [_jsx("span", { className: 'manager__header__sub-title__item', children: "Disease" }), "in", _jsx("span", { className: 'manager__header__sub-title__hierarchy', children: "Disease Profiles" })] }), editMode &&
                        _jsx("div", { className: 'manager__header__exit-edit', onClick: this.headerAction__ExitEdit, children: "Exit Edit" })] }) });
    };
    renderDPColor = () => {
        const dp = this.state.diseaseProfile.item;
        if (!dp.colorGroup)
            return;
        const dpColor = ModuleFE_ColorGroup.cache.unique(dp.colorGroup);
        return _jsx("span", { className: 'manager__header__title__color', style: { background: dpColor.colors[0] }, children: _jsx(ICONS.v.component, { className: dp.completed ? 'completed' : undefined }) });
    };
    renderHeaderActions = () => {
        const dp = this.state.diseaseProfile.item;
        return _jsxs(_Fragment, { children: [_jsx("div", { onClick: this.headerAction__ExportPDF, children: "Export PDF" }), ModuleFE_PermissionsAssert.getAccessLevel(this.DP_PermissionKey_Edit) === AccessLevel.HasAccess && _jsxs(_Fragment, { children: [_jsx("div", { onClick: this.headerAction__editDisease, children: "Edit Disease" }), _jsx("div", { onClick: this.headerAction__addVariable, children: "Add Variable" })] }), ModuleFE_PermissionsAssert.getAccessLevel(this.DP_PermissionKey_Delete) === AccessLevel.HasAccess && _jsxs(_Fragment, { children: [_jsxs("div", { onClick: this.headerAction__PublishDisease, children: [dp.completed ? 'Unp' : 'P', "ublish Disease"] }), _jsx("div", { onClick: this.headerAction__DeleteDisease, children: "Delete Disease" })] })] });
    };
    // ######################## Render - Body ########################
    renderViews = () => {
        const views = this.getOrderedViews();
        const viewProps = (viewId) => {
            return {
                className: _className('manager__disease-profile__views__item', viewId === this.state.selectedViewId && 'selected'),
                onClick: () => this.setView(viewId)
            };
        };
        return _jsxs(LL_V_L, { className: 'manager__disease-profile__views', children: [_jsx("div", { ...viewProps('general'), children: "General" }), views.map(view => {
                    return _jsx("div", { ...viewProps(view._id), children: view.label }, view._id);
                })] });
    };
    renderVars = () => {
        return _jsx(LL_V_L, { className: 'manager__disease-profile__vars', children: this.state.varIds.map(varId => {
                const className = _className('manager__disease-profile__vars__item', this.state.selectedVarId === varId && 'selected');
                return _jsx(LL_V_L, { className: className, onClick: () => this.setState({ selectedVarId: varId }), children: this.renderVarContent(varId) }, varId);
            }) });
    };
    renderVarContent = (varId) => {
        const dp = this.state.diseaseProfile.item;
        if (this.state.selectedViewId === 'general')
            return _jsx(_Fragment, { children: varId });
        const mv = ModuleFE_MultiVar.cache.unique(varId);
        const mainVar = ModuleFE_Variable.cache.unique(mv._firstVarId);
        const variantIds = mv.variables[0].tagIds?.find(i => i.key === dp.variantsGroupId)?.value;
        const ccColor = dp.chiefComplaintIds?.includes(mv._firstVarId) ? '#fb8c4b' : 'transparent';
        return _jsxs(_Fragment, { children: [_jsxs(LL_H_C, { className: 'manager__disease-profile__vars__item__label', children: [_jsx("div", { className: 'manager__disease-profile__vars__item__label__cc', style: { background: ccColor } }), mainVar.name] }), variantIds &&
                    _jsx(LL_V_L, { className: 'manager__disease-profile__vars__item__variants', children: variantIds.map(id => {
                            const tag = ModuleFE_Tag.cache.unique(id);
                            return _jsx("div", { className: 'manager__disease-profile__vars__item__variants__item', children: tag.label }, id);
                        }) })] });
    };
    renderData = () => {
        if (this.state.selectedViewId === 'general')
            return _jsx(Renderer_GeneralInfo, { diseaseProfileId: this.state.diseaseProfile.item._id, varOption: this.state.selectedVarId, editMode: this.state.editMode, triggerChange: this.triggerChange });
        return _jsx(Renderer_DiseaseVariableData, { diseaseProfileId: this.state.diseaseProfile.item._id, diseaseVariableId: this.state.selectedVarId, editMode: this.state.editMode, triggerChange: this.triggerChange });
    };
}
//# sourceMappingURL=Manager_DiseaseProfile.js.map